/**
 * Listagem densa de atendimentos (HNS-1594).
 *
 * Mesmo estilo da listagem de agendamentos: superfície branca sobre o cinza da aplicação,
 * barra de situação na esquerda, encerrados esmaecidos e UMA ação em destaque por linha,
 * com o acessório no menu. Reaproveita as classes `.ag-densa*` do index.css.
 *
 * São nove colunas de conteúdo — lado a lado elas não cabem sem truncar tudo. Em vez de
 * rolagem horizontal, as relacionadas são empilhadas em duas linhas na mesma coluna
 * (paciente/programa, etapa/status, cadastro/modificação), com a segunda em cinza como
 * metadado. É o mesmo recurso dos cards: hierarquia no lugar de largura.
 *
 * A configuração do programa (`colunas_atendimento_padrao`) continua mandando: quando uma
 * coluna é ocultada, some só a linha dela; quando o grupo inteiro é ocultado, some a
 * coluna. Por isso a grade é montada em tempo de execução.
 *
 * É só apresentação: as ações vêm por callback de Atendimentos.jsx.
 */
import React from "react";
import { Dropdown } from "react-bootstrap";
import { FaEye, FaRegEdit, FaTrash, FaCheckDouble, FaChevronDown, FaExclamationTriangle } from "react-icons/fa";
import moment from "moment-timezone";
import { formataDataExibicao, formataDataHoraExibicao, calcularTempoNaEtapa, hasPerm } from "../../common/utils";

// Barra da esquerda: é o que se lê antes de qualquer texto quando a lista é longa.
const COR_ENCERRADO = "#e8edf3";
const COR_ATRASADO = "rgb(235,73,97)";
const COR_HOJE = "rgb(255,193,7)";
const COR_ANDAMENTO = "rgb(94,116,251)";

/**
 * Grupos de colunas. `principal` fica na primeira linha, `secundario` na segunda em cinza.
 * A ordenação do cabeçalho segue o campo principal, como na tabela anterior.
 */
const GRUPOS = [
    {
        chave: "paciente",
        principal: "paciente",
        secundario: "programa",
        largura: "minmax(0, 1.4fr)",
        ordenaPor: "beneficiario_nome",
        //nome de paciente, etapa e laboratorio sao texto livre e longo:
        //quebrar em duas linhas informa mais do que cortar com reticencias
        quebraLinha: true,
    },
    {
        chave: "proxima_acao",
        principal: "proxima_acao",
        largura: "minmax(0, .95fr)",
        ocultaParaPrestador: true,
        ordenaPor: "data",
    },
    {
        chave: "etapa",
        principal: "etapa",
        secundario: "status",
        largura: "minmax(0, 1.7fr)",
        ordenaPor: "etapa_id",
        quebraLinha: true,
    },
    {
        chave: "tempo_etapa",
        principal: "tempo_etapa",
        largura: "minmax(0, .8fr)",
        exigeLiberaColunas: true,
        ordenaPor: "etapa_data_criacao",
        //"4 dias, 21 horas e 3 minutos" nao cabe numa linha; aqui quebra em vez de cortar
        quebraLinha: true,
    },
    {
        chave: "laboratorio",
        principal: "laboratorio",
        largura: "minmax(0, 1fr)",
        ocultaParaPrestador: true,
        quebraLinha: true,
    },
    {
        chave: "datas",
        principal: "data_cadastro",
        secundario: "ultima_modificacao",
        largura: "minmax(0, 1fr)",
        ordenaPor: "data",
        secundarioExigeLiberaColunas: true,
    },
];

const situacaoProximaAcao = (item) => {
    if (!item.proxima_acao?.data_acao) return null;
    const hoje = moment().tz("America/Sao_Paulo");
    const acao = moment.tz(item.proxima_acao.data_acao, "America/Sao_Paulo").startOf("day");
    if (acao.isBefore(hoje, "day")) return "atrasada";
    if (acao.isSame(hoje, "day")) return "hoje";
    return "futura";
};

const LinhasDensasAtendimentos = ({
    itens = [],
    dadosUsuario = {},
    colunasConfig = [],
    permiteEditar = false,
    liberaColunas = false,
    ordem = "",
    ordemDirecao = "",
    onOrdenar,
    onExcluir,
    onValidar,
}) => {
    const ehPrestador = dadosUsuario["tipo_usuario"] === "prestador";
    const podeValidar = dadosUsuario["operador_id"] > 0
        || dadosUsuario["tipo_usuario"] === "gestor"
        || dadosUsuario["tipo_usuario"] === "";

    const configDe = (slug) => colunasConfig.find(c => c.slug === slug);
    const rotuloDe = (slug) => configDe(slug)?.nome || slug;
    const campoVisivel = (slug, exigeLibera = false) => {
        if (!slug) return false;
        if (exigeLibera && !liberaColunas) return false;
        const cfg = configDe(slug);
        //sem configuração o campo aparece, que era o comportamento da tabela
        return cfg ? cfg.exibir_campo !== false : true;
    };

    // um grupo só entra se ao menos uma das suas linhas tiver conteúdo visível
    const grupos = GRUPOS
        .filter(g => !(g.ocultaParaPrestador && ehPrestador))
        .map(g => ({
            ...g,
            mostraPrincipal: campoVisivel(g.principal, g.exigeLiberaColunas),
            mostraSecundario: campoVisivel(g.secundario, g.secundarioExigeLiberaColunas),
        }))
        .filter(g => g.mostraPrincipal || g.mostraSecundario);

    if (itens.length === 0) {
        return (
            <div className="ag-densa">
                <div style={{ padding: "24px 18px", fontSize: "13px", color: "#6b7a8d" }}>
                    Nenhum atendimento encontrado.
                </div>
            </div>
        );
    }

    //Ultima coluna com largura FIXA: cada linha e um grid proprio, entao `auto` mediria
    //o conteudo daquela linha e linhas com menos acoes desalinhariam do cabecalho.
    const grade = ["58px", ...grupos.map(g => g.largura), "190px"].join(" ");
    const estiloGrade = { "--ag-densa-cols": grade };

    const conteudo = (item, slug) => {
        switch (slug) {
            case "paciente":
                return permiteEditar ? item.dados_beneficiario?.nome : `Id: ${item.beneficiario_id}`;
            case "proxima_acao": {
                if (!item.proxima_acao?.data_acao) return "—";
                const hora = (item.proxima_acao.hora_acao || "").split(".")[0].slice(0, 5);
                const sit = situacaoProximaAcao(item);
                return (
                    <>
                        {formataDataExibicao(item.proxima_acao.data_acao)} {hora}
                        {sit === "atrasada" && <FaExclamationTriangle className="text-danger ms-1" />}
                        {sit === "hoje" && <FaExclamationTriangle className="text-warning ms-1" />}
                    </>
                );
            }
            case "laboratorio":
                return item.dados_laboratorio?.nome || "—";
            case "data_cadastro":
                return formataDataHoraExibicao(item.data);
            case "ultima_modificacao":
                return `alterado ${formataDataHoraExibicao(item.data_atualizacao)}`;
            case "programa":
                return item.dados_programa?.nome || "—";
            case "status":
                return item.dados_programa_status?.nome || "—";
            case "etapa":
                return item.cancelado ? "Cancelado" : (item.finalizado ? "Finalizado" : item.dados_etapa?.nome);
            case "tempo_etapa":
                return (!item.finalizado && !item.cancelado && item.dados_etapa_status?.data_cadastro)
                    ? calcularTempoNaEtapa(item.dados_etapa_status.data_cadastro)
                    : "—";
            default:
                return "—";
        }
    };

    const cabecalho = (g) => {
        const rotulos = [
            g.mostraPrincipal ? rotuloDe(g.principal) : null,
            g.mostraSecundario ? rotuloDe(g.secundario) : null,
        ].filter(Boolean);

        const texto = rotulos.join(" · ");
        if (!g.ordenaPor || !onOrdenar || !g.mostraPrincipal) {
            return <span key={g.chave}>{texto}</span>;
        }

        const ativa = ordem === g.ordenaPor;
        return (
            <span
                key={g.chave}
                className="ag-densa__ordenavel"
                role="button"
                tabIndex={0}
                onClick={() => onOrdenar(g.ordenaPor)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOrdenar(g.ordenaPor); }}
                title={`Ordenar por ${rotuloDe(g.principal)}`}
            >
                {texto}
                <span className={`ag-densa__seta${ativa ? " ag-densa__seta--ativa" : ""}`}>
                    {ativa && ordemDirecao === "asc" ? "▲" : ativa ? "▼" : "⇅"}
                </span>
            </span>
        );
    };

    return (
        <div className="ag-densa">
            <div className="ag-densa__grade ag-densa__cabecalho" style={estiloGrade}>
                <span>ID</span>
                {grupos.map(cabecalho)}
                <span />
            </div>

            {itens.map((item) => {
                const encerrado = item.cancelado || item.finalizado;
                const sitAcao = situacaoProximaAcao(item);
                const cor = encerrado ? COR_ENCERRADO
                    : sitAcao === "atrasada" ? COR_ATRASADO
                    : sitAcao === "hoje" ? COR_HOJE
                    : COR_ANDAMENTO;

                const mostrarValidar = podeValidar && (item.programa_id === 3 || item.programa_id === 2);
                const podeExcluir = dadosUsuario?.is_superuser === true;
                const podeAbrirPaciente = hasPerm("add_beneficiarios");
                const temMenu = podeAbrirPaciente || podeExcluir;

                const classes = ["ag-densa__grade", "ag-densa__linha"];
                if (encerrado) classes.push("ag-densa__linha--encerrada");
                if (item.cancelado) classes.push("ag-densa__linha--cancelada");

                return (
                    <div
                        key={item.id}
                        className={classes.join(" ")}
                        style={{ ...estiloGrade, borderLeftColor: cor }}
                    >
                        <span className="ag-densa__id" data-rotulo="ID">{item.id}</span>

                        {grupos.map(g => {
                            //texto puro serve de title; o que trunca fica descobrível no hover
                            const bruto = (slug) => {
                                const v = conteudo(item, slug);
                                return typeof v === "string" ? v : undefined;
                            };
                            const corte = g.quebraLinha ? "" : " text-truncate";

                            return (
                                <span key={g.chave} className="ag-densa__grupo" data-rotulo={rotuloDe(g.principal)}>
                                    {g.mostraPrincipal && (
                                        <span
                                            className={(g.chave === "paciente" ? "ag-densa__paciente" : "ag-densa__servico") + corte}
                                            title={bruto(g.principal)}
                                        >
                                            {conteudo(item, g.principal)}
                                        </span>
                                    )}
                                    {g.mostraSecundario && (
                                        <span className={"ag-densa__sub" + corte} title={bruto(g.secundario)}>
                                            {conteudo(item, g.secundario)}
                                        </span>
                                    )}
                                </span>
                            );
                        })}

                        <span className="ag-densa__acoes">
                            {permiteEditar && (
                                <a className="ag-densa__acao d-inline-flex align-items-center text-decoration-none"
                                   href={`/atendimentos/edit/${item.uuid}`}>
                                    <FaEye className="me-1" /> Visualizar
                                </a>
                            )}

                            {mostrarValidar && (
                                <button
                                    type="button"
                                    className="ag-densa__acao--neutra"
                                    onClick={() => onValidar(item.atendimento_validado, item.id)}
                                    title={item.atendimento_validado ? "Atendimento validado" : "Validar atendimento"}
                                >
                                    <FaCheckDouble className="me-1" />
                                    {item.atendimento_validado ? "Validado" : "Validar"}
                                </button>
                            )}

                            {temMenu && (
                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        as="button"
                                        type="button"
                                        className="ag-densa__mais"
                                        id={`acoes-atend-${item.id}`}
                                        title="Mais ações"
                                    >
                                        <FaChevronDown />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {podeAbrirPaciente && (
                                            <Dropdown.Item href={`/pacientes/edit/${item.beneficiario_id}/?cliente_id=${item.cliente_id}`}>
                                                <FaRegEdit className="me-2" /> Abrir cadastro do paciente
                                            </Dropdown.Item>
                                        )}
                                        {podeExcluir && (
                                            <>
                                                <Dropdown.Divider />
                                                <Dropdown.Item className="text-danger" onClick={() => onExcluir(item)}>
                                                    <FaTrash className="me-2" /> Excluir atendimento
                                                </Dropdown.Item>
                                            </>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default LinhasDensasAtendimentos;
